'use client'

import { useMemo, useState, useSyncExternalStore } from 'react'
import {
  type DehydratedState,
  HydrationBoundary,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { Toast } from '@skop-app/lib-front'
import { useTranslations } from 'next-intl'
import { errorCodes, successCodes } from '@skop-app/lib-domain/constants'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'

import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { computeSelectedTeamState } from '@/features/team-selection/lib/selectedTeam'
import { selectedTeamCookieStore } from '@/features/team-selection/lib/selectedTeamCookieStore'
import {
  buildQueryPersistKey,
  buildQueryPersistScopeKey,
  createIDBPersister,
  lookupOnlyDehydrateOptions,
  QUERY_PERSIST_BUSTER,
  QUERY_PERSIST_MAX_AGE,
} from '@/context/queryPersistence'
import {
  isAbortLikeError,
  isAuthHardFailure,
} from '@/services/auth/authErrorClassifier'
import { getQuotaErrorMessage } from '@/services/errors/quotaErrors'
import {
  shouldSuppressOnboardingMutationErrorToasts,
  shouldSuppressOnboardingMutationSuccessToasts,
} from '@/features/onboarding/onboardingSilentMutations'
import { isMaintenanceModeError } from '@/services/writeMaintenance'
import { performLocalSessionCleanup } from '@/services/auth/logoutCleanup.client'
import { ensureClientAmplifyConfigured } from '@/services/amplify/configureClientAmplify'
import { DeferredReactQueryDevtools } from '@/components/providers/DeferredReactQueryDevtools'

ensureClientAmplifyConfigured()

let authRecoveryPromise: Promise<void> | null = null

async function recoverFromAuthHardFailure(
  queryClient: QueryClient,
  isPublicRuntime: boolean,
): Promise<void> {
  if (isPublicRuntime) return

  if (!authRecoveryPromise) {
    authRecoveryPromise = (async () => {
      await performLocalSessionCleanup(queryClient, {
        clearAppCookies: true,
        clearLegacyAuthState: true,
      })
    })().finally(() => {
      authRecoveryPromise = null
    })
  }

  await authRecoveryPromise
}

function createClientQueryClient(
  isPublicRuntime: boolean,
  maintenanceMessage: string,
  memberRemovalErrorMessage: string,
): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 2, // 2 min
        gcTime: 1000 * 60 * 60, // 1h
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        if (!isAbortLikeError(error) && isAuthHardFailure(error)) {
          void recoverFromAuthHardFailure(queryClient, isPublicRuntime)
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (isMaintenanceModeError(error)) {
          Toast.error(maintenanceMessage, { duration: 5000 })
          return
        }

        const quotaMessage = getQuotaErrorMessage(error)
        if (quotaMessage) {
          Toast.error(quotaMessage, { duration: 4000 })
          return
        }

        if (shouldSuppressOnboardingMutationErrorToasts()) return

        const mutationKey = mutation?.options?.mutationKey?.[0] ?? 'missingKey'
        const message =
          mutationKey === 'deleteMember'
            ? memberRemovalErrorMessage
            : errorCodes[mutationKey as keyof typeof errorCodes]
        if (
          !message ||
          (typeof message === 'string' && message.trim() === '')
        ) {
          return
        }
        Toast.error(message, { duration: 2500 })
      },
      onSuccess(_data, _variables, _context, mutation) {
        if (mutation.meta?.silent) return
        if (shouldSuppressOnboardingMutationSuccessToasts()) return
        const mutationKey = mutation?.options?.mutationKey?.[0] ?? 'missingKey'
        const message = successCodes[mutationKey as keyof typeof successCodes]
        if (
          !message ||
          (typeof message === 'string' && message.trim() === '')
        ) {
          return
        }
        Toast.success(message, { duration: 1000 })
      },
    }),
  })

  return queryClient
}

type DataProviderProps = {
  children: React.ReactNode
  dehydratedState?: DehydratedState
  persistCache?: boolean
}

export const DataProvider = ({
  children,
  dehydratedState,
  persistCache = true,
}: DataProviderProps) => {
  const tMaintenance = useTranslations('WriteMaintenance')
  const tTeam = useTranslations('Team')
  const { sessionScopeKey } = useSessionRuntime()
  const isPublicRuntime = sessionScopeKey === 'public'
  const [queryClient] = useState(() =>
    createClientQueryClient(
      isPublicRuntime,
      tMaintenance('write-rejected'),
      tTeam('remove-member-error'),
    ),
  )

  // Subscribe to cookie changes to pick up external changes (e.g., team switch in another tab)
  const rawSelectedTeam = useSyncExternalStore(
    selectedTeamCookieStore.subscribe,
    selectedTeamCookieStore.getSnapshot,
    selectedTeamCookieStore.getServerSnapshot,
  )
  // `null` is the SSR snapshot, not the absence of a selected-team cookie.
  // PersistQueryClientProvider restores only once, so mounting it before the
  // browser snapshot arrives would permanently restore the solo cache during
  // an organization reload.
  const isPersistScopeResolved = rawSelectedTeam !== null
  const { cookieValue: selectedTeamCookieValue } = computeSelectedTeamState(
    rawSelectedTeam,
    { fallbackToSolo: true },
  )

  // Memoize persister so it's only recreated when the selected team actually changes
  const persister = useMemo(() => {
    const persistScopeKey = buildQueryPersistScopeKey(
      sessionScopeKey,
      selectedTeamCookieValue,
    )
    return createIDBPersister(buildQueryPersistKey(persistScopeKey))
  }, [selectedTeamCookieValue, sessionScopeKey])

  const hydratedChildren = (
    <HydrationBoundary state={dehydratedState}>{children}</HydrationBoundary>
  )

  if (!persistCache || !isPersistScopeResolved) {
    return (
      <QueryClientProvider client={queryClient}>
        {hydratedChildren}
        <DeferredReactQueryDevtools />
      </QueryClientProvider>
    )
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        dehydrateOptions: lookupOnlyDehydrateOptions,
        buster: `${process.env.NEXT_PUBLIC_APP_VERSION ?? 'app'}:${QUERY_PERSIST_BUSTER}`,
        maxAge: QUERY_PERSIST_MAX_AGE,
      }}
    >
      {hydratedChildren}
      <DeferredReactQueryDevtools />
    </PersistQueryClientProvider>
  )
}
